import { useCallback, useEffect, useReducer, useRef, useState } from 'react';
import { api } from '../api.js';
import { estadoInicial, reducir, type Accion_, type Estado } from './estado.js';
import { SITUACION_VACIA, type Obra } from './modelo.js';

/**
 * LO QUE LLEGA DEL SERVIDOR SE SANEA ANTES DE DIBUJARLO.
 *
 * Una obra guardada hace meses puede no tener lo que el modulo da por hecho hoy
 * —una lista de contactos, una situacion, unas coordenadas— porque el modelo ha
 * crecido desde entonces. Cuando falta, la pantalla entera se cae al dibujar, y
 * lo que se pierde no es una obra: son las noventa que estaban bien.
 *
 * Aqui se rellena lo que falte con lo vacio de cada cosa. Es el unico sitio por
 * donde entran las obras, asi que basta con hacerlo una vez.
 */
function sanear(obra: Obra): Obra {
  const numero = (v: unknown, porDefecto = 0): number =>
    typeof v === 'number' && Number.isFinite(v) ? v : porDefecto;
  const lista = <T,>(v: T[] | null | undefined): T[] => (Array.isArray(v) ? v : []);
  return {
    ...obra,
    lat: numero(obra.lat),
    lon: numero(obra.lon),
    progreso: Math.max(0, Math.min(100, Math.round(numero(obra.progreso)))),
    etiquetas: lista(obra.etiquetas),
    contactos: lista(obra.contactos),
    acciones: lista(obra.acciones),
    historial: lista(obra.historial),
    situacion: { ...SITUACION_VACIA, ...(obra.situacion ?? {}) },
  };
}

/**
 * LAS OBRAS DEJAN DE PERDERSE AL RECARGAR.
 *
 * El modulo era una simulacion: el estado vivia en la pestana y se esfumaba con
 * un F5. Se cargan fichas de campo rellenadas a mano, asi que eso ya no vale.
 *
 * El reductor sigue siendo puro y ciego a la red. Aqui se le pone alrededor lo
 * que necesita para durar:
 *
 *   1. AL ENTRAR se pide la lista al servidor.
 *   2. AL CAMBIAR se pinta primero y se guarda despues. Esperar a la respuesta
 *      para mover una tarjeta de etapa haria que la lista pareciera rota en
 *      cualquier conexion mala.
 *   3. SI GUARDAR FALLA se dice. Una pantalla que acepta un cambio y lo pierde
 *      en silencio es peor que una que no lo acepta.
 *
 * Que obra guardar se averigua COMPARANDO referencias: el reductor devuelve
 * objetos nuevos solo para lo que ha tocado. Asi no hay que acordarse de
 * guardar en cada uno de los doce casos del reductor —y olvidarse en el
 * treceavo, que es lo que pasa siempre—.
 */

export interface Obras {
  estado: Estado;
  /**
   * Pinta al momento y guarda por detras. La promesa se cumple cuando el
   * servidor ya lo tiene: quien tenga que colgar algo de la obra —una foto—
   * espera a eso, porque antes la obra todavia no existe alli.
   */
  despachar: (accion: Accion_) => Promise<boolean>;
  /** Aun no ha llegado la primera lista: la pantalla no debe decir «no hay obras». */
  cargando: boolean;
  /** Lo ultimo que no se pudo guardar, para poder avisarlo. */
  errorGuardado: string | null;
  descartarError: () => void;
}

function cambiadas(antes: Obra[], despues: Obra[]): Obra[] {
  const previas = new Map(antes.map((o) => [o.id, o]));
  return despues.filter((o) => previas.get(o.id) !== o);
}

export function useObras(): Obras {
  const [estado, despacharCrudo] = useReducer(reducir, undefined, estadoInicial);
  const [cargando, setCargando] = useState(true);
  const [errorGuardado, setErrorGuardado] = useState<string | null>(null);

  // El reductor se ejecuta aqui tambien, para saber QUE ha cambiado antes de
  // que React vuelva a pintar. `useRef` guarda el estado de verdad porque el de
  // `useReducer` es el de la pasada anterior mientras dura el manejador.
  const actual = useRef<Estado>(estado);
  actual.current = estado;

  useEffect(() => {
    let vivo = true;
    api
      .obras()
      .then(({ obras }) => {
        if (vivo) despacharCrudo({ tipo: 'cargar', obras: obras.map(sanear) });
      })
      .catch(() => {
        /* sin lista no hay nada que pintar; el error de red ya se ve arriba */
      })
      .finally(() => {
        if (vivo) setCargando(false);
      });
    return () => {
      vivo = false;
    };
  }, []);

  const despachar = useCallback(async (accion: Accion_): Promise<boolean> => {
    const antes = actual.current;
    const despues = reducir(antes, accion);
    actual.current = despues;
    despacharCrudo(accion);

    if (accion.tipo === 'cargar' || accion.tipo === 'reiniciar') return true;

    const resultados = await Promise.all(
      cambiadas(antes.obras, despues.obras).map((obra) =>
        api
          .guardarObra(obra)
          .then(() => true)
          .catch(() => {
            setErrorGuardado(obra.codigo || obra.nombre || obra.id);
            return false;
          }),
      ),
    );
    return resultados.every(Boolean);
  }, []);

  return {
    estado,
    despachar,
    cargando,
    errorGuardado,
    descartarError: useCallback(() => setErrorGuardado(null), []),
  };
}
